import React from 'react';

interface RProgressBarProps {
  currentR: number;
  tier: number;
  tier1ThresholdR?: number;
  exitMode?: string;
  takeProfitR?: number;
}

const RProgressBar: React.FC<RProgressBarProps> = ({ currentR, tier, tier1ThresholdR, exitMode, takeProfitR }) => {
  const isFixedTP = exitMode === 'FixedTP';
  const targetR = isFixedTP ? (takeProfitR ?? 0.50) : (tier1ThresholdR ?? 0.30);

  // Bar range: -0.5R to max(1.0R, targetR + 0.3R)
  const minR = -0.5;
  const maxR = Math.max(1.0, targetR + 0.3);
  const range = maxR - minR;

  // Clamp for bar display
  const clampedR = Math.max(minR, Math.min(maxR, currentR));

  // Zero position as percentage
  const zeroPos = ((0 - minR) / range) * 100;
  const targetPos = ((targetR - minR) / range) * 100;

  // Fill position
  const fillStart = currentR >= 0 ? zeroPos : ((clampedR - minR) / range) * 100;
  const fillEnd = currentR >= 0 ? ((clampedR - minR) / range) * 100 : zeroPos;
  const fillWidth = fillEnd - fillStart;

  // Color based on state
  let fillColor = '#64748b'; // gray default
  if (currentR < 0) {
    fillColor = '#ef4444'; // red for loss
  } else if (isFixedTP) {
    // FixedTP: green when approaching TP, blue otherwise
    fillColor = currentR >= targetR * 0.8 ? '#22c55e' : '#3b82f6';
  } else if (tier >= 2) {
    fillColor = '#22c55e'; // green for tier 2
  } else if (tier >= 1) {
    fillColor = '#f59e0b'; // orange for tier 1
  } else if (currentR > 0) {
    fillColor = '#3b82f6'; // blue for profit no tier
  }

  const markerColor = isFixedTP ? '#22c55e' : '#f59e0b';

  return (
    <div className="r-progress-bar">
      <div className="r-bar-track">
        {/* Zero line */}
        <div className="r-bar-zero" style={{ left: `${zeroPos}%` }} />
        {/* Target marker (T1 or TP) */}
        <div className="r-bar-tier1" style={{ left: `${targetPos}%`, borderColor: markerColor }} />
        {/* Fill */}
        <div
          className="r-bar-fill"
          style={{
            left: `${fillStart}%`,
            width: `${fillWidth}%`,
            backgroundColor: fillColor,
          }}
        />
      </div>
      <div className="r-bar-labels">
        <span className="r-label-min">-0.5R</span>
        <span className="r-label-zero">0</span>
        <span className="r-label-tier1" style={{ color: markerColor }}>
          {isFixedTP ? 'TP' : 'T1'}
        </span>
        <span className="r-label-max">{maxR.toFixed(1)}R</span>
      </div>
      {currentR > maxR && (
        <span className="r-overflow" style={{ color: fillColor }}>+{currentR.toFixed(2)}R</span>
      )}
    </div>
  );
};

export default RProgressBar;
